import { useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { Viewshed, RgbaArray, readRgbaArray, dyno } from '@ydrive/sparky';
import * as THREE from 'three';
import type { SparkViewer } from '../utils/SparkViewer';
import { MEDICAL_LAKE_EAST_SIDE_WALL_CHECK, MEDICAL_LAKE_WEST_SIDE_WALL_CHECK, MEDICAL_LAKE_HOUSE1_OCCLUSION_POINT, MEDICAL_LAKE_FRONT_WINDOWS, MEDICAL_LAKE_NEIGHBOR_WINDOWS, MEDICAL_LAKE_PARCEL_OFFSET } from '../config/medicalLakePrivacy';
import { privacyCombinedHouseVisibility, privacyFrontWindowVisibility, privacyDistanceScreen, privacyWallDistanceFeet, privacyOpeningSummary, privacyCombinedCastGeometry, privacyWindowPairCastGeometry, privacyWindowSamples, privacyWindowScreenLabel, privacyScanReceiver, privacyParcelRing, privacyParcelReceiverPanels, privacyHouseClipShader, privacySurfaceObserver, privacyHouseReceiverPanels, privacySurfaceCastGeometry, worldDistance3D } from '../utils/measureUtils';

type Presentation = 'overview' | 'flight' | 'detail';

const SOURCE_ORDER = MEDICAL_LAKE_NEIGHBOR_WINDOWS.map(source => source.id);

export default function PrivacyHouseVisibility({ viewerRef, sourceId, revision, onReady, presentation }: {
  viewerRef: MutableRefObject<unknown>; sourceId: string; revision: number; presentation: Presentation; onReady: (ring: [number, number, number][]) => void;
}) {
  const selectedSource = useRef(sourceId);
  const presentationRef = useRef(presentation);
  const castsReady = useRef(presentation === 'overview');
  const [activeSource] = useState(() => dyno.dynoFloat(-1));
  const [activeCount] = useState(() => dyno.dynoFloat(SOURCE_ORDER.length));
  const [summaries, setSummaries] = useState<Record<string, ReturnType<typeof privacyOpeningSummary>>>({});
  const [frontVisibility, setFrontVisibility] = useState<Record<string, Record<string, {
    label: string; visible: number; evaluated: number; total: number; percent: number | null;
    status: string; obstructions: string[]; blockerPoints: [number, number, number][];
  }>>>({});
  const frontVisibilityRef = useRef(frontVisibility);
  frontVisibilityRef.current = frontVisibility;
  const [combinedSummary, setCombinedSummary] = useState<ReturnType<typeof privacyCombinedHouseVisibility> | null>(null);
  const [status, setStatus] = useState<'checking' | 'ready' | 'unavailable'>('checking');
  const [calculatingPercent, setCalculatingPercent] = useState(() => Math.floor(Math.random() * 21));
  useEffect(() => {
    if (presentation !== 'overview' || status !== 'checking') return;
    const timer = setInterval(() => setCalculatingPercent(Math.floor(Math.random() * 21)), 45);
    return () => clearInterval(timer);
  }, [presentation, status]);
  useLayoutEffect(() => {
    selectedSource.current = sourceId;
    presentationRef.current = presentation;
    activeSource.value = presentation === 'detail' && sourceId !== 'all' ? SOURCE_ORDER.indexOf(sourceId) : -1;
  }, [sourceId, presentation, activeSource]);
  useLayoutEffect(() => {
    // Fly through a clean scene, then let the opening label appear before its cast.
    castsReady.current = presentation === 'overview';
    activeCount.value = castsReady.current ? SOURCE_ORDER.length : 0;
    if (presentation !== 'detail') return;
    const timer = setTimeout(() => {
      castsReady.current = true;
      activeCount.value = SOURCE_ORDER.length;
    }, 450);
    return () => clearTimeout(timer);
  }, [presentation, activeCount]);
  useEffect(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    const mesh = viewer?.splatMesh;
    if (!mesh || !viewer) { setStatus('unavailable'); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const viewsheds: Viewshed[] = [];
    const textures: RgbaArray[] = [];
    const castGroup = new THREE.Group();
    castGroup.name = 'Medical Lake privacy exterior casts';
    viewer.scene.add(castGroup);
    let modifier: (NonNullable<typeof mesh.worldModifiers>)[number] | null = null;
    setStatus('checking');
    const controller = new AbortController();
    Promise.all(['structure-geometry.world.json', 'property-parcels.world.json', 'dsm-minus-dtm.world.json'].map(file =>
      fetch(`/overlays/medical-lake/${file}`, { signal: controller.signal })
        .then(response => { if (!response.ok) throw Error('Property geometry unavailable'); return response.json(); })
    )).then(([data, parcels, grid]) => {
        if (cancelled) return;
        const house = data.features?.find((feature: { properties?: Record<string, unknown> }) => feature.properties?.['target-address'] === true);
        const ring = house?.geometry?.coordinates?.[0]?.[0] as [number, number, number][] | undefined;
        const height = Number(house?.properties?.['roof-characteristics.height']);
        if (!ring || ring.length < 3 || !Number.isFinite(height)) throw Error('Incomplete house geometry');
        const sources = SOURCE_ORDER.flatMap(id => MEDICAL_LAKE_NEIGHBOR_WINDOWS.filter(source => source.id === id));
        const parcel = parcels.features?.find((feature: { properties?: Record<string, unknown> }) => feature.properties?.['target-address'] === true);
        const rawParcel = parcel?.geometry?.coordinates?.[0]?.[0] as [number, number, number][] | undefined;
        if (!rawParcel || rawParcel.length < 3) throw Error('Parcel geometry unavailable');
        const parcelRing = privacyParcelRing(rawParcel, MEDICAL_LAKE_PARCEL_OFFSET);
        const buildings = data.features.flatMap((feature: { geometry?: { coordinates?: [number, number, number][][][] } }) => feature.geometry?.coordinates?.[0]?.[0] ? [feature.geometry.coordinates[0][0]] : []);
        const panels = [...privacyHouseReceiverPanels(ring, height), ...privacyParcelReceiverPanels(parcelRing, grid)];
        const refreshCasts = () => {
          castGroup.children.forEach(object => {
            const cast = object as THREE.Mesh;
            cast.geometry.dispose();
            (cast.material as THREE.Material).dispose();
          });
          castGroup.clear();
          const summary: Record<string, ReturnType<typeof privacyOpeningSummary>> = {};
          const frontResults: typeof frontVisibility = {};
          const frontWindowVolumes: { observer: THREE.Vector3; geometry: THREE.BufferGeometry; id: string; sourceId: string }[] = [];
          const volumes = viewsheds.map((viewshed, sourceIndex) => {
            const source = sources[sourceIndex];
            const observer = privacySurfaceObserver(source.corners, ring, height).observer;
            const sample = (point: THREE.Vector3) => viewshed.sampleVisibility(point);
            summary[source.id] = privacyOpeningSummary(observer, ring, height, sample, buildings, grid);
            frontResults[source.id] = privacyFrontWindowVisibility(MEDICAL_LAKE_FRONT_WINDOWS, sample, observer, buildings, grid);
            const frontWindowView = source.id.startsWith('front-house-');
            if (frontWindowView) {
              MEDICAL_LAKE_FRONT_WINDOWS.forEach(window => {
                if ((frontResults[source.id][window.id]?.visible ?? 0) > 0) frontWindowVolumes.push({
                  observer,
                  geometry: privacyWindowPairCastGeometry(source.corners, window.corners),
                  id: `front-window-cast:${source.id}:${window.id}`,
                  sourceId: source.id,
                });
              });
            }
            const geometry = frontWindowView
              ? new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute([], 3))
              : privacySurfaceCastGeometry(observer, panels, point => sample(point).visible,
                point => privacyScanReceiver(observer, point, sample(point).horizonDistance, parcelRing));
            return { observer, geometry, id: source.id, sourceIndex };
          });
          setSummaries(summary);
          setFrontVisibility(frontResults);
          if (viewsheds.length === sources.length) {
            setCombinedSummary(privacyCombinedHouseVisibility(volumes.map(volume => volume.observer), ring, height,
              (index, point) => viewsheds[index].sampleVisibility(point)));
          }
          const combined = privacyCombinedCastGeometry(volumes.map(volume => volume.geometry));
          const renderVolumes = [
            ...volumes.map(volume => ({ ...volume, sourceId: volume.id.startsWith('front-house-') ? volume.id : undefined })),
            ...frontWindowVolumes,
            { observer: privacyWindowSamples(sources.flatMap(source => source.corners))[0], geometry: combined, id: 'overview', sourceIndex: -1, sourceId: undefined },
          ];
          renderVolumes.forEach(({observer, geometry, id, sourceId}) => {
            if (!geometry.getAttribute('position').count) { geometry.dispose(); return; }
            const planes = geometry.userData.privacyPlanes as THREE.Vector4[];
            if (!planes?.length) { geometry.dispose(); return; }
            const material = new THREE.ShaderMaterial({
              uniforms: { uColor: { value: new THREE.Color(0x006bff) }, uOpacity: { value: 0.68 },
                uEye: { value: new THREE.Vector3() }, uPlanes: { value: planes } },
              vertexShader: `varying vec3 vWorldPosition;
                void main() {
                  vec4 world = modelMatrix * vec4(position, 1.0);
                  vWorldPosition = world.xyz;
                  gl_Position = projectionMatrix * viewMatrix * world;
                }`,
              fragmentShader: `uniform vec3 uColor; uniform float uOpacity; uniform vec3 uEye;
                uniform vec4 uPlanes[${planes.length}]; varying vec3 vWorldPosition;
                void main() {
                  vec3 ray = normalize(vWorldPosition - uEye);
                  float enter = 0.0, leave = 100000.0;
                  for (int i = 0; i < ${planes.length}; i++) {
                    float distance = dot(uPlanes[i].xyz, uEye) + uPlanes[i].w;
                    float slope = dot(uPlanes[i].xyz, ray);
                    if (abs(slope) < 0.00001) { if (distance > 0.01) discard; }
                    else if (slope < 0.0) enter = max(enter, -distance / slope);
                    else leave = min(leave, -distance / slope);
                  }
                  float thickness = max(0.0, leave - enter);
                  if (thickness < 0.001) discard;
                  // Integrate a steady density through the volume, without face seams or pulsing.
                  float alpha = uOpacity * (1.0 - exp(-0.2 * thickness));
                  gl_FragColor = vec4(uColor, alpha);
                  #include <colorspace_fragment>
                }`,
              transparent: true, depthTest: false, depthWrite: false, side: THREE.BackSide, toneMapped: false,
            });
            const cast = new THREE.Mesh(geometry, material);
            cast.name = `privacy-exterior-cast:${id}`;
            cast.renderOrder = 1002;
            cast.onBeforeRender = (_renderer, _scene, camera) => {
              material.uniforms.uEye.value.copy(camera.position);
              const selected = selectedSource.current;
              const shown = castsReady.current && (presentationRef.current === 'overview' ? id === 'overview' || Boolean(sourceId) : presentationRef.current === 'detail' && (selected === id || selected === sourceId));
              material.uniforms.uOpacity.value = shown && worldDistance3D(camera.position, observer) > 2 ? 0.68 : 0;
            };
            castGroup.add(cast);
          });
        };
        let index = 0;
        const computeNext = () => {
          if (cancelled) return;
          try {
            const source = sources[index];
            if (source) {
              const viewshed = new Viewshed(mesh);
              viewsheds.push(viewshed);
              const result = viewshed.compute({ ...privacySurfaceObserver(source.corners, ring, height),
                minRange: 0.5, maxRange: 80, fovDeg: 85, azimuthCells: 1024, elevationCells: 512 });
              if (!result.consideredCount) throw Error('Scene data not available');
              textures.push(viewshed.toRgbaArray(mesh, new RgbaArray({ channels: 1 })));
              refreshCasts();
              index += 1;
              timer = setTimeout(computeNext, 40);
              return;
            }
            const clip = privacyHouseClipShader(parcelRing, null);
            modifier = dyno.dynoBlock({ gsplat: dyno.Gsplat }, { gsplat: dyno.Gsplat }, ({ gsplat }) => {
              if (!gsplat) throw Error('Missing splat');
              const splatIndex = dyno.splitGsplat(gsplat).outputs.index;
              const values = SOURCE_ORDER.map((_, i) => dyno.split(readRgbaArray((textures[i] ?? textures[0]).dyno, splatIndex)).outputs.x);
              const classInputs = Object.fromEntries(values.map((_, i) => [`c${i}`, 'float']));
              const classArgs = Object.fromEntries(values.map((value, i) => [`c${i}`, value]));
              const visibilityTerms = values.map((_, i) => SOURCE_ORDER[i].startsWith('front-house-') ? '0.0' : `(${inputs.count} > ${i}.0 && (${inputs.active} < 0.0 || ${inputs.active} == ${i}.0)) ? ${inputs[`c${i}`]} : 0.0`);
              const visibilityExpression = visibilityTerms.reduce((expression, term) => expression ? `max(${expression}, ${term})` : term, '');
              const tint = new dyno.Dyno({
                inTypes: { gsplat: dyno.Gsplat, ...classInputs, active: 'float', count: 'float' },
                outTypes: { gsplat: dyno.Gsplat },
                globals: () => [clip],
                statements: ({ inputs, outputs }) => [
                  `${outputs.gsplat} = ${inputs.gsplat};`,
                  `if (privacyInsideHouse(${inputs.gsplat}.center) && ${visibilityExpression} >= 1.5 / 255.0) {`,
                  `${outputs.gsplat}.rgba.rgb = mix(${inputs.gsplat}.rgba.rgb, vec3(0.01, 0.24, 1.0), 0.4);`,
                  '}',
                ],
              });
              return { gsplat: tint.apply({ gsplat, ...classArgs, active: activeSource, count: activeCount }).gsplat };
            });
            mesh.worldModifiers = [...(mesh.worldModifiers ?? []), modifier];
            mesh.updateGenerator();
            refreshCasts();
            setStatus('ready');
            const update = () => {
              if (cancelled) return;
              // The native viewshed refreshes streamed pages and its class texture together.
              try {
                const changed = viewsheds.reduce((total, viewshed) => total + viewshed.update({ splatBudget: 131072 }), 0);
                if (changed) refreshCasts();
                timer = setTimeout(update, 250);
              } catch (error) {
                console.warn('[Privacy surfaces] Streaming update unavailable:', error);
                if (modifier) { mesh.worldModifiers = mesh.worldModifiers?.filter(item => item !== modifier); mesh.updateGenerator(); }
                castGroup.visible = false;
                setStatus('unavailable');
              }
            };
            timer = setTimeout(update, 250);
          } catch (error) {
            console.warn('[Privacy surfaces]', error);
            if (!cancelled) setStatus('unavailable');
          }
        };
        onReady(parcelRing);
        timer = setTimeout(computeNext, 0);
      })
      .catch(error => { if (!cancelled) { console.warn('[Privacy surfaces]', error); setStatus('unavailable'); } });
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
      if (modifier) { mesh.worldModifiers = mesh.worldModifiers?.filter(item => item !== modifier); mesh.updateGenerator(); }
      viewer.scene.remove(castGroup);
      castGroup.children.forEach(object => {
        const cast = object as THREE.Mesh;
        cast.geometry.dispose();
        (cast.material as THREE.Material).dispose();
      });
      viewsheds.forEach(viewshed => viewshed.dispose());
      textures.forEach(texture => texture.dispose());
    };
  }, [viewerRef, revision, onReady, activeSource, activeCount]);

  const selectedSummary = summaries[sourceId];
  const selectedOpening = MEDICAL_LAKE_NEIGHBOR_WINDOWS.find(source => source.id === sourceId);
  const selectedIsFrontWindowView = sourceId.startsWith('front-house-');
  const selectedFrontWindowResults = selectedIsFrontWindowView ? MEDICAL_LAKE_FRONT_WINDOWS.map(window => frontVisibility[sourceId]?.[window.id]).filter((result): result is NonNullable<typeof result> => Boolean(result)) : [];
  const selectedFrontVisibleSamples = selectedFrontWindowResults.reduce((total, result) => total + result.visible, 0);
  const selectedFrontTotalSamples = selectedFrontWindowResults.reduce((total, result) => total + result.total, 0);
  const selectedFrontVisibilityPercent = selectedFrontTotalSamples ? Math.round(selectedFrontVisibleSamples / selectedFrontTotalSamples * 100) : null;
  const distanceSvg = useRef<SVGSVGElement | null>(null);
  const distanceLine = useRef<SVGLineElement | null>(null);
  const distanceLabel = useRef<SVGGElement | null>(null);
  const summarySightlineSvg = useRef<SVGSVGElement | null>(null);
  const house1Sightline = useRef<SVGGElement | null>(null);
  const house2Sightline = useRef<SVGGElement | null>(null);
  const house3Sightline = useRef<SVGGElement | null>(null);
  const eastNoView = useRef<SVGGElement | null>(null);
  const westNoView = useRef<SVGGElement | null>(null);
  const house1Callout = useRef<HTMLDivElement | null>(null);
  const house2Callout = useRef<HTMLDivElement | null>(null);
  const house3Callout = useRef<HTMLDivElement | null>(null);
  const eastCallout = useRef<HTMLDivElement | null>(null);
  const westCallout = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    if (!viewer || presentation !== 'detail' || !selectedSummary) return;
    let frame = 0;
    const update = () => {
      const svg = distanceSvg.current, line = distanceLine.current, label = distanceLabel.current;
      if (svg && line && label) {
        const canvas = viewer.renderer.domElement, rect = canvas.getBoundingClientRect();
        const projected = privacyDistanceScreen(selectedSummary, viewer.camera, canvas);
        svg.style.left = `${rect.left}px`; svg.style.top = `${rect.top}px`;
        svg.setAttribute('width', `${rect.width}`); svg.setAttribute('height', `${rect.height}`);
        svg.style.visibility = castsReady.current && projected.label.visible ? 'visible' : 'hidden';
        line.setAttribute('x1', `${projected.start.x}`); line.setAttribute('y1', `${projected.start.y}`);
        line.setAttribute('x2', `${projected.end.x}`); line.setAttribute('y2', `${projected.end.y}`);
        label.setAttribute('transform', `translate(${projected.label.x}, ${projected.label.y})`);
      }
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [viewerRef, presentation, selectedSummary]);

  useEffect(() => {
    const viewer = viewerRef.current as SparkViewer | null;
    const svg = summarySightlineSvg.current;
    if (!viewer || !svg || presentation !== 'overview') return;
    let frame = 0;
    const drawLine = (group: SVGGElement | null, selector: string, from: THREE.Vector3, to: THREE.Vector3) => {
      if (!group) return;
      group.style.visibility = 'visible';
      const canvas = viewer.renderer.domElement;
      const start = privacyWindowScreenLabel(from, viewer.camera, canvas);
      const end = privacyWindowScreenLabel(to, viewer.camera, canvas);
      const lineSelector = selector.includes('target-ray') ? '[data-target-ray-halo]' : '[data-ray-halo]';
      [group.querySelector<SVGLineElement>(lineSelector), group.querySelector<SVGLineElement>(selector)].filter((line): line is SVGLineElement => Boolean(line)).forEach(line => {
        line.setAttribute('x1', `${start.x}`); line.setAttribute('y1', `${start.y}`);
        line.setAttribute('x2', `${end.x}`); line.setAttribute('y2', `${end.y}`);
      });
    };
    const drawCross = (group: SVGGElement | null, point: THREE.Vector3) => {
      if (!group) return;
      const end = privacyWindowScreenLabel(point, viewer.camera, viewer.renderer.domElement);
      group.style.visibility = 'visible';
      const crossA = group.querySelector<SVGLineElement>('[data-cross-a]');
      const crossB = group.querySelector<SVGLineElement>('[data-cross-b]');
      crossA?.setAttribute('x1', `${end.x - 7}`); crossA?.setAttribute('y1', `${end.y - 7}`);
      crossA?.setAttribute('x2', `${end.x + 7}`); crossA?.setAttribute('y2', `${end.y + 7}`);
      crossB?.setAttribute('x1', `${end.x - 7}`); crossB?.setAttribute('y1', `${end.y + 7}`);
      crossB?.setAttribute('x2', `${end.x + 7}`); crossB?.setAttribute('y2', `${end.y - 7}`);
    };
    const placeCallout = (element: HTMLDivElement | null, point: THREE.Vector3, offsetX = 0, offsetY = 0) => {
      if (!element) return;
      const canvas = viewer.renderer.domElement, rect = canvas.getBoundingClientRect();
      const screen = privacyWindowScreenLabel(point, viewer.camera, canvas);
      element.style.visibility = 'visible';
      element.style.translate = `${screen.x + rect.left + offsetX}px ${screen.y + rect.top + offsetY}px`;
      element.dataset.direction = screen.offscreen ? screen.arrow : '';
    };
    const update = () => {
      const rect = viewer.renderer.domElement.getBoundingClientRect();
      svg.style.left = `${rect.left}px`; svg.style.top = `${rect.top}px`;
      svg.setAttribute('width', `${rect.width}`); svg.setAttribute('height', `${rect.height}`);
      const resultsFor = (sourceIds: string[]) => sourceIds.flatMap(id => MEDICAL_LAKE_FRONT_WINDOWS.map(window => ({
        source: MEDICAL_LAKE_NEIGHBOR_WINDOWS.find(item => item.id === id), window,
        result: frontVisibilityRef.current[id]?.[window.id],
      })));
      const updateBlockedRay = (sourceIds: string[], group: SVGGElement | null, callout: HTMLDivElement | null, labelSelector?: string) => {
        const pairs = resultsFor(sourceIds);
        const ready = pairs.length > 0 && pairs.every(pair => pair.result);
        if (!ready || pairs.some(pair => (pair.result?.visible ?? 0) > 0)) {
          if (group) group.style.visibility = 'hidden';
          if (callout) callout.style.visibility = 'hidden';
          return;
        }
        const blockedPair = pairs.find(pair => pair.result?.blockerPoints.length);
        if (!blockedPair?.source || !blockedPair.result) {
          if (group) group.style.visibility = 'hidden';
          if (callout) callout.style.visibility = 'hidden';
          return;
        }
        const source = privacyWindowSamples(blockedPair.source.corners)[0];
        const target = privacyWindowSamples(blockedPair.window.corners)[0];
        const obstruction = new THREE.Vector3(...blockedPair.result.blockerPoints[0]);
        drawLine(group, '[data-ray]', source, obstruction);
        drawLine(group, '[data-target-ray]', obstruction, target);
        drawCross(group, obstruction);
        placeCallout(callout, source.clone().lerp(target, 0.6));
        if (labelSelector) {
          const label = callout?.querySelector<HTMLElement>(labelSelector);
          if (label) label.textContent = `~${Math.round(worldDistance3D(source, target) * 3.280839895)} ft to ${blockedPair.window.label}`;
        }
      };
      const house1 = MEDICAL_LAKE_NEIGHBOR_WINDOWS.find(source => source.id === 'front-house-1');
      const house1Pairs = resultsFor(['front-house-1']);
      if (house1 && house1Pairs.every(pair => pair.result) && house1Pairs.every(pair => pair.result?.visible === 0)) {
        const source = privacyWindowSamples(house1.corners)[0];
        const obstruction = new THREE.Vector3(...MEDICAL_LAKE_HOUSE1_OCCLUSION_POINT);
        const targetWindow = house1Pairs.find(pair => pair.result?.blockerPoints.length)?.window ?? MEDICAL_LAKE_FRONT_WINDOWS[0];
        const target = privacyWindowSamples(targetWindow.corners)[0];
        drawLine(house1Sightline.current, '[data-ray]', source, obstruction);
        drawLine(house1Sightline.current, '[data-target-ray]', obstruction, target);
        drawCross(house1Sightline.current, obstruction);
        placeCallout(house1Callout.current, source.clone().lerp(target, 0.52));
        const callout = house1Callout.current?.querySelector<HTMLElement>('[data-target-distance]');
        if (callout) callout.textContent = `~${Math.round(worldDistance3D(source, target) * 3.280839895)} ft to ${targetWindow.label}`;
      } else {
        if (house1Sightline.current) house1Sightline.current.style.visibility = 'hidden';
        if (house1Callout.current) house1Callout.current.style.visibility = 'hidden';
      }
      updateBlockedRay(['front-house-2'], house2Sightline.current, house2Callout.current, '[data-target-distance]');
      updateBlockedRay(['front-house-3-window-1', 'front-house-3-window-2'], house3Sightline.current, house3Callout.current, '[data-target-distance]');
      const eastStart = new THREE.Vector3(...MEDICAL_LAKE_EAST_SIDE_WALL_CHECK.neighbor), eastEnd = new THREE.Vector3(...MEDICAL_LAKE_EAST_SIDE_WALL_CHECK.home);
      const westStart = new THREE.Vector3(...MEDICAL_LAKE_WEST_SIDE_WALL_CHECK.neighbor), westEnd = new THREE.Vector3(...MEDICAL_LAKE_WEST_SIDE_WALL_CHECK.home);
      drawLine(eastNoView.current, '[data-ray]', eastStart, eastEnd);
      drawLine(westNoView.current, '[data-ray]', westStart, westEnd);
      placeCallout(eastCallout.current, eastStart.clone().lerp(eastEnd, 0.5), 52, -34);
      placeCallout(westCallout.current, westStart.clone().lerp(westEnd, 0.5), 52, 34);
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [viewerRef, presentation]);

  return <>
  {presentation === 'overview' && <div data-privacy-overview-summary className="mt-3 rounded-xl border border-sky-300/30 bg-sky-400/10 p-3" aria-live="polite">
    <div className="text-[10px] font-medium uppercase tracking-wide text-sky-200">Combined exterior visibility</div>
    <div data-privacy-combined-visibility={status === 'checking' ? 'calculating' : status} className={`mt-2 text-4xl font-semibold tracking-tight tabular-nums text-sky-50 ${status === 'checking' ? 'animate-pulse' : ''}`} aria-live="polite">
      {status === 'checking' ? `${String(calculatingPercent).padStart(2, '0')}%` : status === 'ready' && combinedSummary?.visiblePercent != null ? `${combinedSummary.coveragePercent < 95 ? '≥' : '~'}${combinedSummary.visiblePercent}%` : '—'}
    </div>
    <p className="mt-1 text-[11px] leading-relaxed text-sky-100">of your home’s exterior walls are estimated to be visible from at least one measured neighbor opening.</p>
    <p className="mt-2 text-[10px] leading-relaxed text-sky-100/60">Overlapping views count once. Based on all exterior walls; excludes the roof, yard and interiors.</p>
    {combinedSummary && combinedSummary.coveragePercent < 95 && <p className="mt-2 text-[10px] text-sky-100/60">Partial scan: {combinedSummary.coveragePercent}% evaluated. Shown as a lower estimate.</p>}
  </div>}
  {presentation === 'detail' && <div className="mt-3" aria-live="polite" data-privacy-results>
    <div data-privacy-result={sourceId} className="rounded-xl border border-sky-300/30 bg-sky-400/10 p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-sky-200">{selectedIsFrontWindowView ? `Front-window visibility from ${selectedOpening?.label}` : `Exterior visible from ${selectedOpening?.label}`}</div>
      <div className="mt-2 text-4xl font-semibold tracking-tight text-sky-50">{selectedIsFrontWindowView ? status === 'ready' && selectedFrontVisibilityPercent != null ? `${selectedFrontVisibilityPercent}%` : '—' : status === 'ready' && selectedSummary?.visiblePercent != null ? `${selectedSummary.coveragePercent < 95 ? '≥' : '~'}${selectedSummary.visiblePercent}%` : '—'}</div>
      <p className="mt-1 text-[11px] leading-relaxed text-sky-100">{selectedIsFrontWindowView ? 'of sampled points across the front windows of the house have a clear line of sight.' : 'of the outside walls facing this neighbor are estimated to be visible.'}</p>
      {!selectedIsFrontWindowView && selectedSummary && selectedSummary.coveragePercent < 95 && <p className="mt-2 text-[10px] text-sky-100/60">Partial scan: {selectedSummary.coveragePercent}% of sampled walls evaluated. Shown as a lower estimate.</p>}
    </div>
  </div>}
  {presentation === 'detail' && status === 'ready' && selectedSummary && Number.isFinite(selectedSummary.distanceFeet) && createPortal(
    <svg ref={distanceSvg} data-privacy-distance className="pointer-events-none fixed z-[1001] overflow-hidden" style={{ visibility: 'hidden' }} aria-label={`Approximately ${Math.round(selectedSummary.distanceFeet)} feet from ${selectedOpening?.label} to the house`}>
      <line ref={distanceLine} stroke="#a9d8ff" strokeWidth="1.5" strokeDasharray="5 5" />
      <g ref={distanceLabel}>
        <rect x="-48" y="-23" width="96" height="46" rx="9" fill="#123550" fillOpacity="0.96" stroke="#85c4ff" strokeOpacity="0.7" />
        <text y="-3" textAnchor="middle" fill="#f0f9ff" fontSize="15" fontWeight="600">~{Math.round(selectedSummary.distanceFeet)} ft</text>
        <text y="13" textAnchor="middle" fill="#b9d6eb" fontSize="10">to house</text>
      </g>
    </svg>, document.body)}
  {presentation === 'overview' && createPortal(
    <svg ref={summarySightlineSvg} data-privacy-summary-sightlines className="pointer-events-none fixed z-[1001] overflow-hidden" aria-label="Neighbor line of sight summary">
      <g ref={house1Sightline} style={{ visibility: 'hidden' }}>
        <line data-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" />
        <line data-ray stroke="#111827" strokeWidth="3" />
        <line data-target-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-target-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-cross-a stroke="#ff526e" strokeWidth="3" />
        <line data-cross-b stroke="#ff526e" strokeWidth="3" />
      </g>
      <g ref={house2Sightline} style={{ visibility: 'hidden' }}>
        <line data-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-target-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-target-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-cross-a stroke="#ff526e" strokeWidth="3" />
        <line data-cross-b stroke="#ff526e" strokeWidth="3" />
      </g>
      <g ref={house3Sightline} style={{ visibility: 'hidden' }}>
        <line data-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-target-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-target-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-cross-a stroke="#ff9d3d" strokeWidth="3" />
        <line data-cross-b stroke="#ff9d3d" strokeWidth="3" />
      </g>
      <g ref={eastNoView} style={{ visibility: 'hidden' }}>
        <line data-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
      </g>
      <g ref={westNoView} style={{ visibility: 'hidden' }}>
        <line data-ray-halo stroke="#f8fafc" strokeWidth="6" strokeOpacity="0.65" strokeDasharray="2 5" strokeLinecap="round" />
        <line data-ray stroke="#111827" strokeWidth="3" strokeDasharray="2 5" strokeLinecap="round" />
      </g>
    </svg>, document.body)}
  {presentation === 'overview' && createPortal(<>
    <div ref={eastCallout} data-privacy-east-callout className="pointer-events-none fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-white/15 bg-[#111827]/90 px-2 py-1 text-[10px] font-medium leading-tight text-white shadow-lg backdrop-blur-sm" style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>
      <div>~{Math.round(privacyWallDistanceFeet(MEDICAL_LAKE_EAST_SIDE_WALL_CHECK.neighbor, MEDICAL_LAKE_EAST_SIDE_WALL_CHECK.home))} ft</div><div className="text-white/70">No line of sight</div>
    </div>
    <div ref={westCallout} data-privacy-west-callout className="pointer-events-none fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-white/15 bg-[#111827]/90 px-2 py-1 text-[10px] font-medium leading-tight text-white shadow-lg backdrop-blur-sm" style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>
      <div>~{Math.round(privacyWallDistanceFeet(MEDICAL_LAKE_WEST_SIDE_WALL_CHECK.neighbor, MEDICAL_LAKE_WEST_SIDE_WALL_CHECK.home))} ft</div><div className="text-white/70">No line of sight</div>
    </div>
    <div ref={house1Callout} data-privacy-house1-callout className="pointer-events-none fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-white/15 bg-[#111827]/90 px-2 py-1 text-[10px] font-medium leading-tight text-white shadow-lg backdrop-blur-sm" style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>
      <div data-target-distance>No line of sight</div><div className="text-white/70">No line of sight · structure</div>
    </div>
    <div ref={house2Callout} data-privacy-house2-callout className="pointer-events-none fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-white/15 bg-[#111827]/90 px-2 py-1 text-[10px] font-medium leading-tight text-white shadow-lg backdrop-blur-sm" style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>
      <div data-target-distance>No line of sight</div><div className="text-white/70">No line of sight</div>
    </div>
    <div ref={house3Callout} data-privacy-house3-callout className="pointer-events-none fixed left-0 top-0 z-[1002] whitespace-nowrap rounded border border-white/15 bg-[#111827]/90 px-2 py-1 text-[10px] font-medium leading-tight text-white shadow-lg backdrop-blur-sm" style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>
      <div data-target-distance>No line of sight</div><div className="text-white/70">No line of sight</div>
    </div>
  </>, document.body)}
  </>;
}
